import {
  getOnboarding,
  type OnboardingState,
  type OnboardingStepId,
  toggleOnboarding,
  toggleOnboardingStep
} from "@prismicio/editor-fields"
import { useRefGetter } from "@prismicio/editor-support/React"
import { useToast } from "@prismicio/editor-ui"
import { captureException } from "@sentry/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { env } from "../env"
import { useSharedOnboardingGuideFlag } from "../integrations/AmplitudeExperiments"
import { getRepositoryName } from "../routing"

const baseUrl = env.REPOSITORY_BASE_URL
const repository = getRepositoryName()
const authStrategy = env.PAGE_BUILDER_AUTH_STRATEGY
const commonRequestArgs = [baseUrl, repository, authStrategy] as const

async function fetchOnboarding() {
  try {
    return await getOnboarding(...commonRequestArgs)
  } catch (error) {
    captureException(error, { extra: { baseUrl, repository, authStrategy } })
    return null
  }
}

const queryKey = ["shared-onboarding"]

export function useSharedOnboarding() {
  const { showToast } = useToast()
  const isSharedOnboardingEnabled = useIsSharedOnboardingEnabled()

  const queryClient = useQueryClient()
  const { data: onboarding } = useQuery({
    queryKey,
    queryFn: fetchOnboarding,
    enabled: isSharedOnboardingEnabled
  })

  const getOnboardingState = useRefGetter(onboarding)

  async function toggleStep(stepId: OnboardingStepId) {
    if (!isSharedOnboardingEnabled) return []

    // no optimistic update here to avoid having a delay between completing the
    // last step and doing some animation.
    const onboardingState = getOnboardingState()
    if (!onboardingState) return []

    try {
      const { completedSteps } = await toggleOnboardingStep(
        stepId,
        ...commonRequestArgs
      )
      updateOnboarding({ completedSteps })

      return completedSteps
    } catch (error) {
      showToast({ title: "Failed to toggle onboarding step", icon: "alert" })
      return onboardingState.completedSteps
    }
  }

  async function completeStep(...stepIds: OnboardingStepId[]) {
    if (!isSharedOnboardingEnabled) return

    const onboardingState = getOnboardingState()
    if (!onboardingState) return

    try {
      // TODO: Improve this on the API side, it should accept multiple steps (DT-2389)
      const steps = Array.isArray(stepIds) ? stepIds : [stepIds]
      for await (const step of steps) {
        if (!onboardingState.completedSteps.includes(step)) {
          await toggleStep(step)
        }
      }
    } catch (error) {
      showToast({ title: "Failed to complete onboarding step", icon: "alert" })
    }
  }

  async function undoStep(stepId: OnboardingStepId) {
    if (!isSharedOnboardingEnabled) return

    const onboardingState = getOnboardingState()
    if (!onboardingState) return

    try {
      if (onboardingState.completedSteps.includes(stepId)) {
        await toggleStep(stepId)
      }
    } catch (error) {
      showToast({ title: "Failed to undo onboarding step", icon: "alert" })
    }
  }

  async function toggleGuide() {
    if (!isSharedOnboardingEnabled) return

    const onboardingState = getOnboardingState()
    if (!onboardingState) return

    const previousIsDismissed = onboardingState.isDismissed
    try {
      updateOnboarding({ isDismissed: !previousIsDismissed })
      const { isDismissed } = await toggleOnboarding(...commonRequestArgs)
      updateOnboarding({ isDismissed })
    } catch (error) {
      updateOnboarding({ isDismissed: previousIsDismissed })
      showToast({ title: "Failed to toggle onboarding", icon: "alert" })
    }
  }

  function updateOnboarding(newData: Partial<OnboardingState>) {
    queryClient.setQueriesData(
      { queryKey },
      (current: OnboardingState | undefined) => {
        if (!current) return current
        return { ...current, ...newData }
      }
    )
  }

  return { onboarding, toggleStep, completeStep, undoStep, toggleGuide }
}

export function useIsSharedOnboardingEnabled() {
  return useSharedOnboardingGuideFlag() === "with-shared-onboarding"
}
